import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

// Renders 1 random active ad for `placement` (and optional `categoryName`).
// `variant: "card" | "banner"` — sidebar uses card, top-of-page uses banner.
// Renders nothing if no ads (display:none).
export default function AdSlot({ placement, categoryName, variant = "card" }) {
  const [ad, setAd] = useState(undefined); // undefined=loading, null=none, object=ad
  const [imgFailed, setImgFailed] = useState(false);
  const isCategory = placement === "category";

  useEffect(() => {
    let cancelled = false;
    setImgFailed(false);
    const params = { placement, limit: 4 };
    if (isCategory && categoryName) params.category = categoryName;
    api
      .get("/ads/active", { params })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.results || [];
        if (list.length === 0) {
          setAd(null);
        } else {
          setAd(list[Math.floor(Math.random() * list.length)]);
        }
      })
      .catch(() => !cancelled && setAd(null));
    return () => {
      cancelled = true;
    };
  }, [placement, categoryName, isCategory]);

  if (ad === undefined || ad === null) return null;
  // When the ad image fails, keep the slot visible with a solid branded block
  // (iter 15 — stop ads from vanishing mid-render).

  const click = `${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.id}/click`;
  const img = ad.image_url?.startsWith("http")
    ? ad.image_url
    : `${process.env.REACT_APP_BACKEND_URL}${ad.image_url || ""}`;

  if (variant === "banner") {
    return (
      <a
        href={click}
        target="_blank"
        rel="noreferrer sponsored"
        className="card-surface card-hover relative flex flex-col gap-4 overflow-hidden p-0 sm:flex-row"
        data-testid={`ad-slot-${placement}`}
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-accent sm:aspect-auto sm:w-2/5">
          {!imgFailed && (
            <img src={img} alt={ad.headline} className="h-full w-full object-cover" loading="lazy" onError={() => setImgFailed(true)} />
          )}
          <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">Ad</span>
        </div>
        <div className="flex flex-1 flex-col justify-center p-5 sm:p-7">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Sponsored</span>
          <h3 className="mt-2 font-display text-2xl font-bold leading-tight text-primary">{ad.headline}</h3>
          <p className="mt-3 text-sm text-muted-foreground">Visit advertiser →</p>
        </div>
      </a>
    );
  }

  return (
    <a
      href={click}
      target="_blank"
      rel="noreferrer sponsored"
      className="card-surface card-hover relative block overflow-hidden"
      data-testid={`ad-slot-${placement}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-primary-dark via-primary to-accent">
        {!imgFailed && (
          <img src={img} alt={ad.headline} className="h-full w-full object-cover" loading="lazy" onError={() => setImgFailed(true)} />
        )}
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary">Ad</span>
      </div>
      <div className="p-4">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-accent-dark">Sponsored</span>
        <h4 className="mt-1 font-display text-lg font-bold leading-tight text-primary line-clamp-2">{ad.headline}</h4>
      </div>
    </a>
  );
}

// Small dashboard sub-nav: Listings ↔ Ads
export function DashboardSubNav({ active }) {
  const tabs = [
    { key: "listings", label: "Listings", to: "/dashboard" },
    { key: "ads", label: "Ads", to: "/dashboard/ads" },
  ];
  return (
    <div className="mt-6 flex gap-2 border-b border-border" data-testid="dashboard-subnav">
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
            active === t.key
              ? "border-accent text-primary"
              : "border-transparent text-muted-foreground hover:text-primary"
          }`}
          data-testid={`subnav-${t.key}`}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}
